import React from 'react';
import { motion } from "framer-motion";
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const CancellationPolicy = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
        {/* Animated Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <motion.div
            animate={{ x: [0, 40, 0], y: [0, 60, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
            className="absolute top-[10%] -left-[5%] w-[35%] h-[35%] bg-primary/5 rounded-full blur-[100px]"
          />
          <motion.div
            animate={{ x: [0, -70, 0], y: [0, 90, 0] }}
            transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-[20%] -right-[5%] w-[30%] h-[30%] bg-accent/5 rounded-full blur-[100px]"
          />
        </div>

        <section id="cancellation-policy" className="relative z-10 pt-32 pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              {/* Header */}
              <div className="text-center mb-16 space-y-4">
                <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
                  Refunds & <span className="text-primary">Cancellations</span>
                </h1>
                <p className="text-muted-foreground font-medium uppercase tracking-widest text-xs">Terms • Returns • Refunds</p>
                <p className="text-muted-foreground mt-2">Effective Date: January 30, 2026</p>
              </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-muted/50 rounded-2xl p-8 md:p-12 space-y-8 text-foreground border border-border">
                
                {/* Platform Info */}
                <div className="p-4 bg-background border border-border rounded-lg mb-8">
                  <p className="text-muted-foreground"><strong>Platform:</strong> Quro (Website & Mobile Application)</p>
                  <p className="text-muted-foreground"><strong>Operated By:</strong> Quick Retail Online Private Limited (“Quro”, “We”, “Us”, “Our”)</p>
                  <p className="text-muted-foreground text-sm mt-1"><strong>FSSAI Reg. No.:</strong> 30260424124243466</p>
                </div>

                {/* 1. DEFINITIONS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">1. DEFINITIONS</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">For the purpose of these Terms:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li><strong>“Customer” / “User”</strong> means any person placing an order on Quro.</li>
                    <li><strong>“Platform”</strong> means the Quro mobile application and website.</li>
                    <li><strong>“Order”</strong> refers to any purchase made through the Platform.</li>
                    <li><strong>“Wallet”</strong> refers to the Quro Wallet maintained within the app.</li>
                    <li><strong>“Perishable Goods”</strong> include fresh food, dairy, meat, seafood, fruits, vegetables, and cooked food.</li>
                    <li><strong>“Non-Perishable Goods”</strong> include packaged items, groceries, and household products.</li>
                  </ul>
                </div>

                {/* 2. ACCEPTANCE OF TERMS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">2. ACCEPTANCE OF TERMS</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">By accessing or using the Quro platform, placing an order, or requesting a return/refund, the user:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Acknowledges having read and understood these Terms</li>
                    <li>Agrees to be bound by them</li>
                    <li>Accepts Quro’s decision as final in case of disputes</li>
                  </ul>
                  <p className="leading-relaxed mt-4 text-muted-foreground italic">If you do not agree to these Terms, you must not use the Platform.</p>
                </div>

                {/* 3. RETURN & REFUND ELIGIBILITY */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">3. RETURN & REFUND ELIGIBILITY</h2>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">3.1 General Conditions</h3>
                  <p className="leading-relaxed mb-4 text-muted-foreground">A return or refund request is valid only if:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Raised through the Quro App → My Orders → Help</li>
                    <li>Submitted within the allowed time window</li>
                    <li>Accompanied by clear photographic evidence</li>
                    <li>The product matches the complaint raised</li>
                  </ul>
                  <p className="leading-relaxed mt-4 text-muted-foreground">Quro reserves the right to reject any request that does not meet these conditions.</p>
                </div>

                {/* 4. CATEGORY-WISE RETURN POLICY */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">4. CATEGORY-WISE RETURN POLICY</h2>
                  
                  {/* 4.1 Cold Storage & Perishable Items */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2 text-foreground">4.1 Cold Storage & Perishable Items</h3>
                    <p className="text-muted-foreground mb-2"><strong>Includes:</strong> Meat, seafood, dairy, frozen/chilled items, fresh produce.</p>
                    <p className="text-muted-foreground mb-2 font-semibold underline">Conditions:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground mb-4">
                      <li>Must be reported within 1 hour of delivery</li>
                      <li>Must be in original packaging</li>
                      <li>Photo proof mandatory</li>
                    </ul>
                    <p className="text-muted-foreground font-semibold">Refunds are allowed only if:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Item is spoiled or unsafe</li>
                      <li>Incorrect item delivered</li>
                      <li>Packaging is damaged or tampered</li>
                      <li>Temperature abuse is evident</li>
                    </ul>
                  </div>

                  {/* 4.2 Sweets, Snacks & Restaurant Food */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2 text-foreground">4.2 Sweets, Snacks & Restaurant Food</h3>
                    <div className="bg-red-500/10 border-l-4 border-red-500 p-4 mb-4">
                      <p className="text-red-700 font-bold">❌ No return or refund is allowed once the item is opened or consumed.</p>
                    </div>
                    <p className="text-muted-foreground mb-2 font-semibold">Refunds are considered only if:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>The item is unopened</li>
                      <li>Wrong item was delivered</li>
                      <li>Packaging was damaged at delivery</li>
                      <li>Product was spoiled on arrival</li>
                    </ul>
                    <p className="mt-4 text-muted-foreground italic text-sm">Once opened, tasted, or partially consumed, the item becomes strictly non-returnable.</p>
                  </div>

                  {/* 4.3 Non-Perishable Items */}
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-foreground">4.3 Non-Perishable Items</h3>
                    <p className="text-muted-foreground mb-2 font-semibold">Returns are accepted if:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground mb-4">
                      <li>Wrong item delivered</li>
                      <li>Manufacturing defect</li>
                      <li>Item damaged during transit</li>
                    </ul>
                    <p className="text-muted-foreground mb-2 font-semibold underline">Conditions:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Request raised within 72 hours</li>
                      <li>Item unused and in original packaging</li>
                      <li>Photo or video evidence provided</li>
                    </ul>
                  </div>
                </div>

                {/* 5. REFUND PROCESS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">5. REFUND PROCESS</h2>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">5.1 Mode of Refund</h3>
                  <p className="leading-relaxed mb-4 text-muted-foreground">All approved refunds will be processed as:</p>
                  <div className="bg-green-500/10 border-l-4 border-green-500 p-4 mb-4">
                    <p className="text-green-700 font-bold">✅ Instant Credit to Quro Wallet</p>
                  </div>
                  <p className="text-muted-foreground mb-4 font-semibold italic">Wallet credits:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Are non-transferable</li>
                    <li>Cannot be withdrawn as cash</li>
                    <li>Can only be used for future orders on Quro</li>
                  </ul>
                  <p className="leading-relaxed mt-4 text-muted-foreground italic">In rare cases, refunds to original payment method may take 5–7 working days.</p>

                  <h3 className="text-xl font-semibold mt-8 mb-2 text-foreground">5.2 Refund Approval</h3>
                  <p className="text-muted-foreground mb-4">Refunds are subject to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground mb-4">
                    <li>Verification of claim</li>
                    <li>Image validation</li>
                    <li>Internal quality checks</li>
                  </ul>
                  <p className="text-muted-foreground mb-2 font-semibold">Quro reserves the right to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                    <li>Partially approve refunds</li>
                    <li>Reject claims without valid proof</li>
                    <li>Block repeated misuse</li>
                  </ul>
                </div>

                {/* 6. NON-RETURNABLE ITEMS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">6. NON-RETURNABLE ITEMS</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">The following items are strictly non-returnable:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground grid grid-cols-1 md:grid-cols-2 gap-2">
                    <li>Opened food or beverages</li>
                    <li>Consumed restaurant items</li>
                    <li>Personal care or hygiene products</li>
                    <li>Cosmetics once opened</li>
                    <li>Gift cards or digital items</li>
                    <li>Items reported beyond the allowed time</li>
                    <li>Items without photographic evidence</li>
                  </ul>
                </div>

                {/* 7. CUSTOMER RESPONSIBILITIES */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">7. CUSTOMER RESPONSIBILITIES</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">The customer agrees to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Verify orders at delivery</li>
                    <li>Store items as per instructions</li>
                    <li>Report issues within timelines</li>
                    <li>Provide accurate information</li>
                    <li>Avoid misuse of refund policy</li>
                  </ul>
                  <p className="leading-relaxed mt-4 text-muted-foreground italic">Failure to comply may result in rejection of future claims.</p>
                </div>

                {/* 8. FRAUD & MISUSE POLICY */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">8. FRAUD & MISUSE POLICY</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">Quro reserves the right to take action against:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>False refund claims</li>
                    <li>Repeated abuse of refund system</li>
                    <li>Fake or reused images</li>
                    <li>Claims for consumed items</li>
                  </ul>
                  <p className="leading-relaxed mt-4 text-muted-foreground font-semibold">Actions may include:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                    <li>Account suspension or termination</li>
                    <li>Wallet balance forfeiture</li>
                    <li>Blocking future orders</li>
                    <li>Legal proceedings where applicable</li>
                  </ul>
                </div>

                {/* 9. REPLACEMENT POLICY */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">9. REPLACEMENT POLICY</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">Quro does not offer direct product replacements. All valid cases are resolved through:</p>
                  <div className="bg-primary/10 border-l-4 border-primary p-4">
                    <p className="text-primary font-bold">✔ Refunds to Quro Wallet</p>
                  </div>
                </div>

                {/* 10. FORCE MAJEURE */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">10. FORCE MAJEURE</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">Quro shall not be liable for delays or failures caused by:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Natural disasters</li>
                    <li>Government restrictions</li>
                    <li>Logistics disruptions</li>
                    <li>System failures beyond control</li>
                  </ul>
                </div>

                {/* 11. CUSTOMER SUPPORT */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">11. CUSTOMER SUPPORT</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="p-4 bg-background border border-border rounded-lg">
                      <p className="font-semibold text-foreground mb-2">Contact Details:</p>
                      <p className="text-muted-foreground">📧 Email: <a href="mailto:customer_service@quro.today" className="text-primary hover:underline">customer_service@quro.today</a></p>
                      <p className="text-muted-foreground">📱 App: Help → Support</p>
                    </div>
                    <div className="p-4 bg-background border border-border rounded-lg">
                      <p className="font-semibold text-foreground mb-2">Response Time:</p>
                      <p className="text-muted-foreground">Urgent issues: within 2 hours</p>
                      <p className="text-muted-foreground">General issues: within 24 hours</p>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-background border border-border rounded-lg">
                    <div className="text-muted-foreground space-y-1">
                      <p className="font-semibold text-foreground">Registered Office:</p>
                      <p>Quick Retail Online Private Limited</p>
                      <p>24-21, Ashoknagar</p>
                      <p>Ramachandra Puram, Hyderabad-502032</p>
                    </div>
                    <div className="text-muted-foreground mt-4 space-y-1">
                      <p className="font-semibold text-foreground">HQ Office:</p>
                      <p>Suite 406, 4th floor, MyScape Weave</p>
                      <p>Financial District, Nankramaguda</p>
                      <p>Hyderabad-500032</p>
                    </div>
                  </div>
                </div>

                {/* 12. GOVERNING LAW & JURISDICTION */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">12. GOVERNING LAW & JURISDICTION</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    These Terms shall be governed by the laws of India. All disputes shall be subject to the exclusive jurisdiction of courts located in Hyderabad, Telangana.
                  </p>
                </div>

                {/* 13. MODIFICATIONS TO TERMS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">13. MODIFICATIONS TO TERMS</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    Quro reserves the right to update or modify these Terms at any time. Continued use of the Platform after updates constitutes acceptance of the revised Terms.
                  </p>
                </div>

                {/* 14. FINAL ACKNOWLEDGEMENT */}
                <div className="bg-primary/5 p-8 rounded-xl border border-primary/20 text-center">
                  <h2 className="text-2xl font-bold mb-4 font-display">14. FINAL ACKNOWLEDGEMENT</h2>
                  <p className="leading-relaxed text-muted-foreground">By placing an order on Quro, the customer confirms that:</p>
                  <p className="font-medium mt-4">They have read and understood this policy • They agree to all terms • They accept Quro’s final decision</p>
                </div>

              </div>
            </div>

            {/* Back to Top Button */}
            <div className="mt-12 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <span>Back to Top</span>
              </button>
            </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default CancellationPolicy;
